import { ref, readonly, onMounted } from 'vue';

/**
 * Composable para detectar bots de performance e SEO e fazer bypass de modais
 * Detecta Googlebot, Lighthouse, GTmetrix, PageSpeed Insights, etc.
 */
export const usePerformanceBotBypass = () => {
  const isBot = ref(false);
  const botName = ref<string | null>(null);

  /**
   * Detecta se o user agent é de um bot conhecido
   */
  const detectBot = () => {
    if (import.meta.server) {
      return false;
    }

    // Evita múltiplas detecções se já foi detectado
    if (isBot.value) {
      return true;
    }

    const userAgent = navigator.userAgent.toLowerCase();

    // Lista de bots conhecidos para detecção
    const botPatterns = [
      { name: 'Googlebot', pattern: /googlebot/i },
      { name: 'Google PageSpeed', pattern: /google.*speed/i },
      { name: 'Lighthouse', pattern: /lighthouse/i },
      { name: 'Chrome-Lighthouse', pattern: /chrome-lighthouse/i },
      { name: 'GTmetrix', pattern: /gtmetrix/i },
      { name: 'PageSpeed Insights', pattern: /speed.*insights/i },
      { name: 'Pingdom', pattern: /pingdom/i },
      { name: 'WebPageTest', pattern: /webpagetest/i },
      { name: 'Headless Chrome', pattern: /headlesschrome/i },
      { name: 'PhantomJS', pattern: /phantomjs/i },
      { name: 'Puppeteer', pattern: /puppeteer/i },
    ];

    // Verifica cada padrão de bot
    for (const bot of botPatterns) {
      if (bot.pattern.test(userAgent)) {
        botName.value = bot.name;
        isBot.value = true;

        return true;
      }
    }

    // Detecta características de navegadores headless
    // Chrome headless usa "HeadlessChrome" no user agent
    if (userAgent.includes('headless')) {
      botName.value = 'Headless Browser';
      isBot.value = true;

      return true;
    }

    // Detecta ausência de plugins (comum em bots)
    // Nota: navigator.plugins está deprecated, mas ainda é útil para detectar bots
    // @ts-ignore - navigator.plugins é deprecated mas necessário para detecção de bots
    if (
      typeof navigator.plugins !== 'undefined' &&
      navigator.plugins.length === 0 &&
      navigator.webdriver
    ) {
      botName.value = 'Automated Browser';
      isBot.value = true;

      return true;
    }

    // Detecta presença da propriedade webdriver
    if (navigator.webdriver) {
      botName.value = 'WebDriver Browser';
      isBot.value = true;

      return true;
    }

    return false;
  };

  /**
   * Bypass automático de cookies para bots
   * Define cookies como aceitos para evitar modal
   */
  const bypassCookieConsent = () => {
    if (!isBot.value) return;

    // Define o cookie de consentimento
    const cactusCookiesConsent = useCookie<string | string[]>(
      'cactusCookiesConsent',
      {
        default: () => ['necessary', 'performance', 'functionality', 'others'],
        path: '/',
        expires: new Date(new Date().setDate(new Date().getDate() + 365)),
      },
    );

    // Aceita todos os cookies automaticamente para bots
    cactusCookiesConsent.value = [
      'necessary',
      'performance',
      'functionality',
      'others',
    ];
  };

  /**
   * Bypass automático de verificação de idade para bots
   * Define cookie de idade verificada
   */
  const bypassLegalAge = () => {
    if (!isBot.value) return;

    // Define o cookie de idade verificada
    const legalAgePopupClosed = useCookie('legal_age_popup_closed', {
      default: () => true,
      path: '/',
      expires: new Date(new Date().setDate(new Date().getDate() + 30)),
    });

    legalAgePopupClosed.value = true;
  };

  /**
   * Executa todos os bypasses automaticamente
   */
  const bypassModalsForBots = () => {
    if (!isBot.value) return;

    bypassCookieConsent();
    bypassLegalAge();

    // Log para debug (apenas em dev)
    // @ts-ignore - import.meta.dev está correto para Nuxt 3+
    if (import.meta.dev) {
      console.log(`[Bot Detection] Bot detectado: ${botName.value}`);
      console.log('[Bot Detection] Modais de cookies e idade desabilitados');
    }
  };

  // Detecta bot imediatamente (síncrono) para uso em middlewares e composables
  // que podem executar antes do onMounted
  if (!import.meta.server) {
    detectBot();
  }

  // Executa bypass de modais quando o componente montar
  onMounted(() => {
    bypassModalsForBots();
  });

  return {
    isBot: readonly(isBot),
    botName: readonly(botName),
    detectBot,
    bypassCookieConsent,
    bypassLegalAge,
    bypassModalsForBots,
  };
};
